import { useMemo } from "react";

import AttachedCloudIntegrationsSection from "components/AttachCloud/Details/CloudIntegrationsSection";
import DocumentationButton from "components/DocumentationButton";
import { EmptystateCloudColored } from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import PermissionGateButton from "components/PermissionGate/Button";
import Box from "ds/components/Box";
import Callout from "ds/components/Callout";
import EmptyState from "ds/components/EmptyState";
import useTypedContext from "hooks/useTypedContext";
import useAttachableCloudIntegrations from "shared/CloudIntegration/useAttachableCloudIntegrations";
import { isSelfHostedDistribution } from "utils/distribution";
import { getDocsUrl } from "utils/getDocsUrl";

import ModuleSettingsFormWrapper from "../components/FormWrapper";
import { ModuleSettingsContext } from "../Context";
import { showModuleSettingsIntegrationsAttachCloudIntegrationsDrawer } from "./AttachCloudIntegrationsDrawer";
import ModuleSettingsIntegrationsPageLayout from "./components/PageLayout";
import useAttachedCloudIntegrations from "./hooks/useAttachedCloudIntegrations";
import useAttachedGcpIntegration from "./hooks/useAttachedGcpIntegration";
import styles from "./styles.module.css";

const isSelfHosted = isSelfHostedDistribution();

const ModuleSettingsIntegrationsCloudIntegrations = () => {
  const { module, canManageModule } = useTypedContext(ModuleSettingsContext);

  const {
    loading: cloudConfigLoading,
    cloudConfig,
    attachableAzureIntegrations,
    attachableAwsIntegrations,
    hasData,
    refetch,
    refetching,
  } = useAttachableCloudIntegrations(module.spaceDetails.id);

  const { loading: integrationsLoading, integrations } = useAttachedCloudIntegrations(module.id);

  const { loading: gcpIntegrationLoading, integration: gcpIntegration } = useAttachedGcpIntegration(
    module.id
  );

  const manuallyAttachedIntegrations = useMemo(() => {
    const manualIntegrations = integrations.filter((integration) => {
      if ("isAutoattached" in integration) {
        return !integration.isAutoattached;
      }
      return true; // if not autoattached, it's a manually attached integration
    });

    if (gcpIntegration?.activated) {
      manualIntegrations.push(gcpIntegration);
    }

    return manualIntegrations;
  }, [integrations, gcpIntegration]);

  const autoAttachedIntegrations = useMemo(() => {
    return integrations.filter((integration) => {
      if ("isAutoattached" in integration) {
        return integration.isAutoattached;
      }
      return false;
    });
  }, [integrations]);

  const attachedIds = useMemo(
    () =>
      integrations
        // filter out GCP integration because it's handled separately in the attach drawer.
        .filter((integration) => "integrationId" in integration)
        .map((integration) => integration.integrationId),
    [integrations]
  );

  const filteredAwsIntegrations = useMemo(
    () => attachableAwsIntegrations.filter((integration) => !attachedIds.includes(integration.id)),
    [attachableAwsIntegrations, attachedIds]
  );

  const filteredAzureIntegrations = useMemo(
    () =>
      attachableAzureIntegrations.filter((integration) => !attachedIds.includes(integration.id)),
    [attachableAzureIntegrations, attachedIds]
  );

  const hasSomeIntegrationsAttached = integrations.length > 0 || gcpIntegration?.activated;

  const hasAttachableCloudIntegrations = hasData && cloudConfig.length > 0;

  const canAttachGcpIntegration = !gcpIntegration?.activated && !isSelfHosted;

  const canAttach =
    filteredAwsIntegrations.length > 0 ||
    filteredAzureIntegrations.length > 0 ||
    canAttachGcpIntegration;

  const loading = cloudConfigLoading || integrationsLoading || gcpIntegrationLoading;

  const handleAttachIntegrationDrawer = () => {
    showModuleSettingsIntegrationsAttachCloudIntegrationsDrawer({
      moduleId: module.id,
      hasData,
      refetch,
      refetching,
      cloudConfig,
      filteredAzureIntegrations,
      filteredAwsIntegrations,
      canAttachGcpIntegration,
    });
  };

  return (
    <>
      <ModuleSettingsIntegrationsPageLayout
        actions={
          canAttach && (
            <PermissionGateButton
              variant="primary"
              access={canManageModule}
              onPress={handleAttachIntegrationDrawer}
            >
              Attach cloud integration
            </PermissionGateButton>
          )
        }
      >
        {!loading && (
          <Callout variant="info">
            You can only attach integrations from the current space and parent spaces that you
            inherit from.
          </Callout>
        )}

        {loading && <PageLoading />}

        {!loading && hasAttachableCloudIntegrations && !hasSomeIntegrationsAttached && (
          <Box align="center" justify="center" grow="1" fullWidth>
            <EmptyState
              padding="large"
              icon={EmptystateCloudColored}
              title="You do not have any attached cloud integrations yet"
              caption="You can attach one of the existing ones, or create a new one."
            >
              <Box gap="medium">
                {canAttach && (
                  <PermissionGateButton
                    variant="primary"
                    access={canManageModule}
                    onPress={handleAttachIntegrationDrawer}
                  >
                    Attach cloud integration
                  </PermissionGateButton>
                )}
                <DocumentationButton
                  to={getDocsUrl("/integrations/cloud-providers")}
                  label="Documentation"
                />
              </Box>
            </EmptyState>
          </Box>
        )}

        {!loading && !hasAttachableCloudIntegrations && !hasSomeIntegrationsAttached && (
          <Box align="center" justify="center" grow="1" fullWidth>
            <EmptyState
              padding="large"
              icon={EmptystateCloudColored}
              title="You do not have any cloud integrations yet"
              caption={
                <>
                  Cloud Integrations are used to dynamically generate short-lived credentials to
                  authenticate to cloud providers (AWS/Azure/GCP currently supported). In this way
                  you avoid using static credentials and by doing so, you reduce breaches.
                  <br /> Read more in the Documentation.
                </>
              }
            >
              <DocumentationButton
                to={getDocsUrl("/integrations/cloud-providers")}
                label="Documentation"
              />
            </EmptyState>
          </Box>
        )}

        {!loading && hasSomeIntegrationsAttached && (
          <Box
            direction="column"
            gap="x-large"
            fullWidth
            fullHeight
            className={styles.integrationsBackground}
          >
            <ModuleSettingsFormWrapper gap="large" fullWidth padding="0">
              <AttachedCloudIntegrationsSection
                title="Attached directly"
                integrations={manuallyAttachedIntegrations}
                cloudConfig={cloudConfig}
                stackId={module.id}
                readOnly={!canManageModule.result}
                stackLabels={module.labels}
                refetchQueries={[
                  "GetAttachedModuleIntegrations",
                  "GetModuleAttachedGcpIntegration",
                ]}
              />
              <AttachedCloudIntegrationsSection
                title="Auto-attached"
                integrations={autoAttachedIntegrations}
                cloudConfig={cloudConfig}
                stackId={module.id}
                readOnly={true} // auto-attached integrations can't be detached by the user
                isAutoAttached={true}
                stackLabels={module.labels}
              />
            </ModuleSettingsFormWrapper>
          </Box>
        )}
      </ModuleSettingsIntegrationsPageLayout>
    </>
  );
};

export default ModuleSettingsIntegrationsCloudIntegrations;
